import { useEffect, useId, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons';
import type { EbookQuestion } from '@wx-weiqi/contracts';
import type { ClassroomTextbook } from '../services/classroom-textbooks';
import './classroom-sgf-panel.css';
import './classroom-textbooks.css';

function questionTitle(question: EbookQuestion, index: number) {
  return (
    [question.number.trim(), question.title.trim()].filter(Boolean).join(' · ') ||
    `第 ${index + 1} 题`
  );
}

export function ClassroomTextbookQuestionList({
  textbook,
  selectedId,
  busy,
  onSelect,
}: {
  readonly textbook: ClassroomTextbook;
  readonly selectedId: string | null;
  readonly busy: boolean;
  readonly onSelect: (question: EbookQuestion) => void;
}) {
  return textbook.questions.length === 0 ? (
    <p className="studio-textbook-state">
      该教材尚未加入编排题目。请在“我的教材”的编排中添加题目并保存。
    </p>
  ) : (
    <>
      <p className="studio-textbook-list-hint">
        {textbook.title} · 已编排 {textbook.questions.length} 题 · 选择后自动收起
      </p>
      <div className="studio-sgf-list-scroll">
        {textbook.questions.map((question, index) => (
          <button
            key={question.id}
            type="button"
            aria-pressed={question.id === selectedId}
            disabled={busy}
            onClick={() => onSelect(question)}
          >
            <span className="studio-sgf-index">{index + 1}</span>
            <span className="studio-sgf-file-detail">
              <strong>{questionTitle(question, index)}</strong>
              <small>
                {question.chapter.trim() ? `${question.chapter} · ` : ''}
                {question.size} 路 · {question.currentPlayer === 'BLACK' ? '黑先' : '白先'}
              </small>
            </span>
            {question.id === selectedId ? <CheckIcon aria-hidden="true" /> : null}
          </button>
        ))}
      </div>
    </>
  );
}

export function ClassroomTextbookPanel({
  textbook,
  selectedId,
  busy,
  onSelect,
  onChoose,
  onClose,
}: {
  readonly textbook: ClassroomTextbook;
  readonly selectedId: string | null;
  readonly busy: boolean;
  readonly onSelect: (question: EbookQuestion) => void;
  readonly onChoose: () => void;
  readonly onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const selectedIndex = textbook.questions.findIndex((question) => question.id === selectedId);
  const selected = textbook.questions[selectedIndex];
  const currentDescription = selected
    ? questionTitle(selected, selectedIndex)
    : `已编排 ${textbook.questions.length} 题 · 请选择题目`;

  useEffect(() => setExpanded(false), [textbook]);

  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !panelRef.current?.contains(event.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [expanded]);

  return (
    <aside
      ref={panelRef}
      className="studio-sgf-panel studio-textbook-panel"
      role="dialog"
      aria-label="教材题目窗口"
      aria-busy={busy}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        if (expanded) {
          setExpanded(false);
          toggleRef.current?.focus();
        } else onClose();
      }}
    >
      <div className="studio-sgf-toolbar">
        <button
          ref={toggleRef}
          className="studio-sgf-toggle"
          type="button"
          disabled={busy}
          title={`${textbook.title} · ${currentDescription}`}
          aria-label={`${expanded ? '收起' : '展开'}教材题目列表：${textbook.title}，${currentDescription}`}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((value) => !value)}
        >
          <span className="studio-sgf-tag">教材</span>
          <span className="studio-sgf-filename">
            {textbook.title} · {selected ? questionTitle(selected, selectedIndex) : '请选择题目'}
          </span>
          <small>
            {selected ? `${selectedIndex + 1} / ` : ''}
            {textbook.questions.length} 题
          </small>
          <ChevronDownIcon className={expanded ? 'is-expanded' : ''} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="studio-sgf-upload"
          disabled={busy}
          onClick={() => {
            setExpanded(false);
            onChoose();
          }}
        >
          更换教材
        </button>
        <button
          type="button"
          className="studio-sgf-close"
          aria-label="关闭教材题目列表"
          title="关闭列表，保留当前棋盘与课堂笔记"
          onClick={onClose}
        >
          <Cross2Icon aria-hidden="true" />
        </button>
      </div>
      {expanded ? (
        <div id={listId} className="studio-sgf-list" role="group" aria-label="已编排教材题目">
          <ClassroomTextbookQuestionList
            textbook={textbook}
            selectedId={selectedId}
            busy={busy}
            onSelect={(question) => {
              onSelect(question);
              setExpanded(false);
              toggleRef.current?.focus();
            }}
          />
        </div>
      ) : null}
    </aside>
  );
}
